import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api } from "@/api/client";
import { useAuth } from "@/auth/AuthProvider";
import { useStore } from "@/store/StoreContext";
import { Badge, tx } from "@/components/ui";
import { fmtLongDate, loc } from "@/lib/dates";

type Today = { date: string; appointments: number; by_status: Record<string, number>; revenue?: string; value?: string; waiting: number; missing_checkouts: number; working_now: number; upcoming: { id: string; starts_at: string; customer: string; service: Record<string, string>; staff: string | null; status: string }[] };

function Card({ label, value, to, tone }: { label: string; value: string | number; to?: string; tone?: "amber" | "red" }) {
  const cls = `rounded-xl border bg-white p-4 ${tone === "amber" ? "border-amber-300" : tone === "red" ? "border-red-300" : ""}`;
  const body = <><div className="text-xs text-gray-500">{label}</div><div className="text-2xl font-semibold">{value}</div></>;
  return to ? <Link to={to} className={`${cls} block hover:bg-gray-50`}>{body}</Link> : <div className={cls}>{body}</div>;
}

/** Home: today in the selected store — the numbers, what is coming up, and what needs a hand. */
export function Dashboard() {
  const { me } = useAuth();
  const { store } = useStore();
  const { t, i18n } = useTranslation();
  const today = useQuery({ queryKey: ["stats-today", store?.id], queryFn: () => api<Today>(`/app/stats/today?store_id=${store!.id}`), enabled: !!store, refetchInterval: 60000 });
  const d = today.data;
  const time = (iso: string) => new Date(iso).toLocaleTimeString(loc(i18n.language), { hour: "2-digit", minute: "2-digit", timeZone: store?.tz });
  const tone = { scheduled: "blue", checked_in: "green", completed: "gray", cancelled: "red", no_show: "red" } as const;
  return (
    <div className="space-y-5">
      <div><h1 className="text-2xl font-semibold">{t("dashboard.welcome", { name: me?.name })}</h1><p className="text-sm text-gray-500">{store ? `${tx(store.name, i18n.language)} · ${fmtLongDate(new Date(), i18n.language)}` : ""}</p></div>
      {d && (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-6">
          <Card label={t("dashboard.todayAppointments")} value={d.appointments} to={`/app/calendar?date=${d.date}`} />
          <Card label={t("status.completed")} value={d.by_status.completed ?? 0} />
          <Card label={t("status.no_show")} value={d.by_status.no_show ?? 0} />
          {d.revenue !== undefined && <Card label={t("stats.k.revenue")} value={`${Number(d.revenue).toFixed(2)} €`} to="/app/stats" />}
          <Card label={t("dashboard.workingNow")} value={d.working_now} to="/app/attendance" />
          <Card label={t("wlp.title")} value={d.waiting} to={`/app/calendar?date=${d.date}`} />
          <Card label={t("issues.title")} value={d.missing_checkouts} tone={d.missing_checkouts ? "amber" : undefined} />
        </div>
      )}
      <div className="grid gap-4 lg:grid-cols-[1fr_320px]">
        <section className="rounded-xl border bg-white">
          <div className="flex items-center justify-between border-b px-4 py-2"><h2 className="font-medium">{t("dashboard.upNext")}</h2><Link className="text-xs underline" to="/app/calendar">{t("nav.calendar")}</Link></div>
          <ul className="divide-y text-sm">
            {d?.upcoming.map((a) => (
              <li key={a.id}><Link className="flex items-center gap-3 px-4 py-2 hover:bg-gray-50" to={`/app/calendar?date=${d.date}&appt=${a.id}`}><span className="w-12 font-medium">{time(a.starts_at)}</span><span className="flex-1 truncate">{a.customer} · {tx(a.service, i18n.language)}{a.staff ? ` · ${a.staff}` : ""}</span><Badge tone={tone[a.status as keyof typeof tone] ?? "gray"}>{t(`status.${a.status}`)}</Badge></Link></li>
            ))}
            {d && d.upcoming.length === 0 && <li className="px-4 py-3 text-gray-500">{t("dashboard.nothingLeft")}</li>}
          </ul>
        </section>
        <section className="space-y-2 rounded-xl border bg-white p-4 text-sm">
          <h2 className="font-medium">{t("dashboard.quick")}</h2>
          <Link className="block rounded-lg border px-3 py-2 hover:bg-gray-50" to="/app/calendar">+ {t("calendar.new")}</Link>
          <Link className="block rounded-lg border px-3 py-2 hover:bg-gray-50" to="/app/customers">{t("customers.new")}</Link>
          <Link className="block rounded-lg border px-3 py-2 hover:bg-gray-50" to="/app/attendance">{t("att.checkIn")} / {t("att.checkOut")}</Link>
          {me?.roles.some((r) => ["owner", "admin", "manager", "accountant"].includes(r.role)) && <Link className="block rounded-lg border px-3 py-2 hover:bg-gray-50" to="/app/stats">{t("stats.title")}</Link>}
        </section>
      </div>
    </div>
  );
}
